import { LoginErrorCode, type ErrorCode } from "@ai-platform/contracts";

/*
 * The texts of the login (08 §1, AP-F5): one per code the callback sends back as
 * `/login?error=<code>`, and one per error `POST /api/auth/login/start` answers with. The API
 * decides which code applies; the portal only shows the text. Each message is a short title and
 * a sentence with the way on, as in the start version of the design prototype (B-40 to B-46,
 * AP-F10.1). Customer-facing German in the "ihr" form; wording is for marketing to adjust here,
 * in one place.
 */

export type LoginMessage = { title: string; text: string };

/** One message per `LoginErrorCode`; a new code fails the typecheck until it has its text. */
export const loginErrorTexts: Record<LoginErrorCode, LoginMessage> = {
  state: { title: "Diese Anmeldung ließ sich nicht zuordnen.", text: "Bitte startet sie noch einmal." },
  expired: { title: "Die Anmeldung ist abgelaufen.", text: "Bitte startet sie noch einmal." },
  // also a refused or suspended account and a customer at the staff entry (ADR-104)
  denied: {
    title: "Die Anmeldung wurde abgebrochen oder abgelehnt.",
    text: "Versucht es noch einmal. Habt ihr sie nicht selbst abgebrochen, wendet euch an den Support.",
  },
  upstream: {
    title: "Eure dataCycle-Instanz antwortet gerade nicht.",
    text: "Bitte versucht es in ein paar Minuten noch einmal.",
  },
  // the dialog offers "Für die Beta bewerben" with this text (ADR-093 point 8; in the alpha
  // release in place of "Zugang anfragen", TL 2026-10-27); a mistyped address ends here too,
  // hence the check first (TL, 2026-10-12)
  instance_not_enabled: {
    title: "Diese Instanz ist für feratelAIplus noch nicht freigeschaltet.",
    text: "Prüft die Adresse. Stimmt sie, bewerbt euch für die Beta – wir melden uns.",
  },
  instance_mismatch: {
    title: "Diese E-Mail-Adresse gehört zu einer anderen Instanz.",
    text: "Meldet euch mit dem Konto der richtigen Instanz an oder wendet euch an den Support.",
  },
  other_instance: {
    title: "Euer Konto gehört bereits zu einer anderen dataCycle-Instanz.",
    text: "Bitte wendet euch an den Support.",
  },
};

/** For a code the portal does not know, e.g. from an older or newer API. */
export const LOGIN_ERROR_FALLBACK: LoginMessage = {
  title: "Die Anmeldung hat nicht geklappt.",
  text: "Bitte versucht es noch einmal.",
};

/**
 * The message for `?error=` of the callback: `null` without one, the code's message for a known
 * code, the general one for anything else.
 */
export function loginErrorText(value: unknown): LoginMessage | null {
  if (value === undefined || value === null || value === "") return null;
  const code = LoginErrorCode.safeParse(value);
  return code.success ? loginErrorTexts[code.data] : LOGIN_ERROR_FALLBACK;
}

/** Errors of `login/start` with their own message (07 §2.3); every other code gets the general one. */
export const loginStartErrorTexts: Partial<Record<ErrorCode, LoginMessage>> = {
  rate_limited: {
    title: "Zu viele Versuche.",
    text: "Bitte wartet ein paar Minuten, bevor ihr es noch einmal versucht.",
  },
  // the platform only starts the login over the instances it allows (ADR-126); here the host came
  // from the dialog's instance field, not from the e-mail lookup, so the message speaks of the
  // address and says nothing about other instances
  instance_mismatch: {
    title: "Die Anmeldung über diese Instanz ist nicht möglich.",
    text: "Prüft die Adresse eurer dataCycle-Instanz. Stimmt sie, wendet euch an den Support.",
  },
};

export const LOGIN_START_FALLBACK: LoginMessage = {
  title: "Die Anmeldung konnte nicht starten.",
  text: "Bitte versucht es noch einmal.",
};

/**
 * The lookup of the e-mail address did not answer (`upstream`, 08 §1): the dialog offers the
 * instance field instead, and this message says why.
 */
export const LOOKUP_UNAVAILABLE_TEXT: LoginMessage = {
  title: "Eure E-Mail-Adresse lässt sich gerade nicht prüfen.",
  text: "Gebt stattdessen die Adresse eurer dataCycle-Instanz an.",
};

/** The message for a failed `login/start` by the API's error code; without a code the general one. */
export function loginStartErrorText(code: ErrorCode | undefined): LoginMessage {
  return (code && loginStartErrorTexts[code]) ?? LOGIN_START_FALLBACK;
}
